import { useMemo, useState } from 'react';
import { computeLayout } from '@/layout/computeLayout';
import { mainSpec, type MainShareBasis } from '@/layout/specs';
import type { StageId, ViewId } from '@/layout/types';
import { useFunnelResult } from '@/hooks/useFunnelResult';
import { track } from '@/lib/analytics';
import { useFunnelStore } from '@/store/useFunnelStore';
import { StageDrawer } from './Drawer/StageDrawer';
import { EditIcon } from './StageIcon';
import { ConversionMetricsTable } from './ConversionMetricsTable';
import { DegradedBanner } from './DegradedBanner';
import { ExplodedModal } from './ExplodedModal';
import { HistoryPanel } from './History/HistoryPanel';
import { Sankey } from './Sankey/Sankey';

interface Props {
  /** The access code, or null when running without a scenario (tests). */
  scenarioId?: string | null;
}

export function Canvas({ scenarioId = null }: Props) {
  const { result, display } = useFunnelResult();
  const [shareBasis, setShareBasis] = useState<MainShareBasis>('population');
  const view = useFunnelStore((s) => s.view);
  const setView = useFunnelStore((s) => s.setView);
  const selectedStage = useFunnelStore((s) => s.selectedStage);
  const openStage = useFunnelStore((s) => s.openStage);

  /* The base canvas is always the full pathway; an exploded step opens over it. */
  const spec = useMemo(
    () => mainSpec(result, display, shareBasis),
    [result, display, shareBasis],
  );
  const layout = useMemo(() => computeLayout(spec), [spec]);

  const exploded = view !== 'main';

  const activate = ({ stage, explode }: { stage?: StageId; explode?: ViewId }) => {
    if (explode) {
      track('view_exploded', { view: explode });
      setView(explode);
      return;
    }
    if (stage) {
      track('drawer_opened', { stage });
      openStage(stage, {
        completed: result.nodes.completed,
        screeningRate: result.rates.screeningRate,
      });
    }
  };

  return (
    <main className="canvas">
      {/* Hidden from assistive tech and from focus while a modal covers it. */}
      <div className="stage" aria-hidden={exploded || undefined} inert={exploded || undefined}>
        <DegradedBanner />
        <header className="canvas-header">
          <h1 className="canvas-title">Vitalic Health Depression Screening and Follow Up</h1>
          {/* The cards are editable too, but only once you know that. This is the
              signposted way in, and it lands on the first stage of the pathway. */}
          <button
            type="button"
            className="update-funnel"
            onClick={() => activate({ stage: 'source' })}
          >
            <EditIcon />
            Update Funnel
          </button>
        </header>
        <div className="share-toggle-row">
          <div className="share-toggle" role="group" aria-label="Show percentages as">
            {(['population', 'previous'] as const).map((basis) => (
              <button
                key={basis}
                type="button"
                aria-pressed={shareBasis === basis}
                onClick={() => setShareBasis(basis)}
              >
                % of {basis}
              </button>
            ))}
          </div>
        </div>
        <div className="flow-frame">
          <Sankey
            layout={layout}
            selectedStage={selectedStage}
            selectable
            onActivate={activate}
          />
        </div>
        <ConversionMetricsTable result={result} display={display} shareBasis={shareBasis} />
      </div>

      <ExplodedModal result={result} display={display} />
      <StageDrawer result={result} display={display} />
      <HistoryPanel scenarioId={scenarioId} />
    </main>
  );
}
